// ===== Imports =====
import React, {useCallback, useEffect, useState} from 'react';
import classNames from 'classnames';
import {useFormikContext} from 'formik';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Launch} from '@material-ui/icons';

import {actions} from '../../../store';
import {ReduxState} from '../../../store/types';
import {Colors} from '../../../styles/Colors';
import {AiFormInterface, AiFormType, useStyles} from './AiForm.utils';
import {Agent, AiAgentsResponse} from '../../../store/types/AiAgent';

import SwitchWithLabel from '../../SwitchWithLabel/SwitchWithLabel';
import {AiIcon} from '../../Extensions/AiIcon';
import AsyncSelectField from '../../AsyncSelectField/AsyncSelectField';
import Button from '../../Button/Button';
import BarLoader from '../../BarLoader/BarLoader';

// ===== Component =====
const AiForm: React.FC<AiFormInterface> = ({
    emailIsPresent,
    countryIsPresent,
    adapterAvailable,
    initSessionFailed,
    isInitialLoading,
    onAiEnabledChange,
    handleDirtyChange,
    handleSetSubmitFunc,
    handleSetIsValidFunc,
}) => {
    // ===== Hooks & Context =====
    const classes = useStyles();
    const {t} = useTranslation();
    const {values, setFieldValue, dirty, handleSubmit, isValid, errors} =
        useFormikContext<AiFormType>();

    // ===== Store Selectors =====
    const dispatch = useDispatch();
    const tenant = useSelector((s: ReduxState) => s.aiAgent.tenant);

    const [dropdownOpenKey, setDropdownOpenKey] = useState(0);
    const onMenuOpen = useCallback(() => setDropdownOpenKey((k) => k + 1), []);

    // ===== Memoized Handlers =====
    const localSubmitHandler = React.useCallback(() => {
        handleSubmit();
    }, [handleSubmit]);

    const setAiEnabled = useCallback(
        (val: boolean) => {
            setFieldValue('aiEnabled', val);
        },
        [setFieldValue],
    );

    const onChangeList = useCallback(
        (value: any) => {
            const {label, ...agent} = value ?? {};
            setFieldValue('aiAgent', value ? agent : null);
        },
        [setFieldValue],
    );

    const onPortalClick = useCallback(() => {
        if (tenant?.sign_in_link) {
            window.open(tenant.sign_in_link);
        }
    }, [tenant?.sign_in_link]);

    const isOptionSelected = useCallback(
        (option: any) => option?.agent_id === values.aiAgent?.agent_id,
        [values.aiAgent],
    );

    const formatAgentOption = useCallback(
        (a: Agent) => ({
            ...a,
            label: a.phone_number === null ? `${a.name} (phone not assigned)` : a.name,
        }),
        [],
    );

    const loadOptions = useCallback(
        async (search: string, prevOptions: readonly unknown[] = []) => {
            const limit = 50;
            const offset = prevOptions.length;

            if (!tenant?.access_token) return {options: [], hasMore: false};

            try {
                const data: AiAgentsResponse = await (dispatch(
                    actions.loadAiAgentsPage({
                        ...(search ? {name: search} : {}),
                        limit,
                        offset,
                    }),
                ) as unknown as Promise<AiAgentsResponse>);
                const agents = data.agents ?? [];
                const total = data.pagination?.total_records ?? offset + agents.length;
                return {
                    options: agents.map(formatAgentOption),
                    hasMore: total > offset + agents.length,
                };
            } catch {
                return {options: [], hasMore: false};
            }
        },
        [tenant?.access_token, formatAgentOption, dispatch],
    );

    // ===== Effects =====
    useEffect(() => {
        handleDirtyChange?.('ai', dirty);
    }, [dirty]);
    useEffect(() => {
        handleSetSubmitFunc?.('ai', localSubmitHandler);
    }, [handleSetSubmitFunc, localSubmitHandler]);
    useEffect(() => {
        handleSetIsValidFunc?.('ai', () => isValid);
    }, [isValid]);

    // A failed init-session (after the saga's retry) locks the toggle in both directions —
    // AI cannot be configured until the tenant provisions, so we surface a tooltip instead of
    // leaving a non-functional switch. Otherwise: when AI is ON only initial load blocks it
    // (user must always be able to disable AI); when OFF, the prerequisites gate enabling.
    const toggleDisabled =
        Boolean(initSessionFailed) ||
        (values.aiEnabled
            ? Boolean(isInitialLoading)
            : !emailIsPresent || !countryIsPresent || !adapterAvailable || Boolean(isInitialLoading));

    const toggleTooltip = initSessionFailed
        ? t('tooltips:extensions.aiInitSessionFailed')
        : values.aiEnabled
            ? undefined
            : !emailIsPresent
                ? t('tooltips:extensions.reqEmailToEnable')
                : !countryIsPresent
                    ? t('tooltips:extensions.reqCountryToEnable')
                    : !adapterAvailable
                        ? t('tooltips:extensions.aiAdapterUnavailable')
                        : undefined;

    // ===== Render =====
    return (
        <div className={classes.root} data-testid={'ai-form'}>
            <div className={classes.container}>
                <SwitchWithLabel
                    id="aiEnabled"
                    field="aiEnabled"
                    label={t('screens:extensions.aiAgent', 'AI agent')}
                    value={values.aiEnabled}
                    disabled={toggleDisabled}
                    setValue={() => {
                        const newVal = !values.aiEnabled;
                        setAiEnabled(newVal);
                        onAiEnabledChange?.(newVal);
                    }}
                    icon={<AiIcon dataQa={'ai-toggle-icon'} title={'AI agent'} />}
                    className={classes.swicher}
                    dataQa={'ai-enabled-component'}
                    customDisabledTooltip={toggleTooltip}
                />

                {isInitialLoading && (
                    <div
                        className={classes.skeleton}
                        data-qa="ai-form-loader"
                        data-testid="ai-form-loader"
                    >
                        <div className={classes.rowContainer}>
                            <div className={classes.skeletonField}>
                                <BarLoader
                                    dataTestId="ai-form-skeleton-input"
                                    backgroundColor={Colors.Gray4}
                                />
                            </div>
                        </div>
                        <div className={classes.skeletonButton}>
                            <BarLoader
                                dataTestId="ai-form-skeleton-button"
                                backgroundColor={Colors.Gray4}
                            />
                        </div>
                    </div>
                )}

                {values.aiEnabled && !isInitialLoading && (
                    <div>
                        <div className={classes.rowContainer}>
                            <AsyncSelectField
                                title={t('screens:extensions.aiAgent')}
                                loadOptions={loadOptions}
                                value={
                                    values.aiAgent
                                        ? {
                                              ...values.aiAgent,
                                              label:
                                                  values.aiAgent.phone_number === null
                                                      ? `${values.aiAgent.name} (phone not assigned)`
                                                      : values.aiAgent.name,
                                          }
                                        : null
                                }
                                onChange={onChangeList}
                                isOptionSelected={isOptionSelected}
                                dataQa="agent-input"
                                disableClearable
                                required
                                helperText={errors.aiAgent as string}
                                className={classes.input}
                                onMenuOpen={onMenuOpen}
                                cacheUniqs={[dropdownOpenKey]}
                            />
                        </div>
                        <div>
                            <Button
                                dataQa="open-ai-configuration-button"
                                primary
                                accent
                                className={classNames(classes.buttonStyle)}
                                disabled={!tenant?.sign_in_link}
                                onClick={onPortalClick}
                                icon={<Launch htmlColor={Colors.Gray5} />}
                            >
                                {t('screens:extensions.aiAgentAccessLink')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ===== Export =====
export default AiForm;
